import { QuestionTagCategory, useGetTagsByCategoryQuery } from '@exam-management/graphql';
import { useQBankStructure } from '@exam-management/qbs-provider';
import { Table, TableContainer, type TableContainerProps } from '@shared/components';

import { QBankStructureDashBoardTableHeader } from '../../components';
import {
  formatDataToQBankStructureSingleQuestionDashboardTable,
  getShallowestEnabledNonCourseLevel,
} from '../../utils';
import { QBankStructureSingleQuestionTableBody } from '../qbank-structure-single-question-dashboard-table-body';

import { type SingleQuestionPropertiesData } from './type';

interface QBankStructureSingleQuestionDashBoardTableProps extends TableContainerProps {
  data: SingleQuestionPropertiesData[];
}

export default function QBankStructureSingleQuestionDashBoardTable(
  props: Readonly<QBankStructureSingleQuestionDashBoardTableProps>,
) {
  const { data, ...restProps } = props;

  const { qBankStructureLevelItems, qBankStructureLevels } = useQBankStructure();

  const defaultLevel = getShallowestEnabledNonCourseLevel(qBankStructureLevels);

  const { data: getDifficultyTagsResult } = useGetTagsByCategoryQuery({
    variables: { input: { category: QuestionTagCategory.Difficulty } },
  });

  const { data: getCalculationTagsResult } = useGetTagsByCategoryQuery({
    variables: { input: { category: QuestionTagCategory.Calculation } },
  });

  const difficultyTags = getDifficultyTagsResult?.qbsGetTagsByCategory ?? [];
  const calculationTags = getCalculationTagsResult?.qbsGetTagsByCategory ?? [];
  const formattedData = formatDataToQBankStructureSingleQuestionDashboardTable({
    qBankStructureLevelItems,
    singleQuestionPropertiesData: data,
    difficultyTags,
    calculationTags,
  });

  if (!defaultLevel) {
    return null;
  }

  return (
    <TableContainer
      {...restProps}
      sx={{
        overflow: 'hidden',
        ...restProps.sx,
      }}
    >
      <Table
        stickyHeader
        size='small'
        sx={{
          border: 'none',
          borderCollapse: 'initial',
          mb: '-1px',
        }}
      >
        <QBankStructureDashBoardTableHeader />
        <QBankStructureSingleQuestionTableBody
          rows={formattedData}
          defaultLevel={defaultLevel.category}
        />
      </Table>
    </TableContainer>
  );
}
